import { FMFile } from "../shared/FMFile";
import GTable from "../libs/gtable/GTable";
import Utils from "../libs/Utils";
import { ReactElement, useState } from "react";
import FileMenu, { IContextMenu } from "./FileMenu";
import { DescriptionOutlined, FolderOutlined, ImageOutlined, InsertDriveFileOutlined } from "@mui/icons-material";
import { useDialog } from "../libs/dialog/DialogProvider";
import Files from "../fmanlibs/Files";
import { useSnackbar } from "../libs/dialog/SnackbarProvider";
import ConfirmDelete from "../dialogs/ConfirmDelete";
import Usernames from "../fmanlibs/Usernames";
import { usePath } from "../libs/path/PathProvider";
import { Checkbox } from "@mui/material";
import _ from "lodash";
import SelectionMenu from "./SelectionMenu";
import FManApi from "../fmanlibs/FManApi";
import ShareDlg from "../dialogs/ShareDlg";

const COLS = ["name","updated","size","createdBy"]
const LABELS : Record<string,string> = {
    name:"Name",
    updated:"Date modified",
    size:"Size",
    createdBy:"Owner"
}

const G = 1_000_000_000
const M = 1_000_000
const K = 1_000

function size(val:number){
    if (val>=G) return (val/G).toFixed(2)+" GB"
    else if (val>M) return (val/M).toFixed(2)+" MB"
    else if (val>K) return (val/K).toFixed(2)+" KB"
    else return val
}

function FileName(props:{file:FMFile,selected:boolean,onToggle:(selected:boolean)=>void}) {
    const {file,selected,onToggle} = props
    let icon:ReactElement
    if (FMFile.isDir(file)) icon = <FolderOutlined/>
    else if (FMFile.isImage(file)) icon = <ImageOutlined/>
    else if (FMFile.isText(file)) icon = <DescriptionOutlined/>
    else icon = <InsertDriveFileOutlined/>
    return <div className="filename flex middle gap-4">
        <Checkbox checked={selected} onChange={e=>onToggle(e.target.checked)}/>
        {icon} {props.file.name}
    </div>
}
export default function FileList(props:{files:FMFile[]}) {
    const [menu,setMenu] = useState<IContextMenu|undefined>()
    const [selected,setSelected] = useState<string[]>([])
    const {openDialog} = useDialog()
    const {setSnackbar} = useSnackbar()
    const {setPath,setHash} = usePath()

    const selectedFiles = () => props.files.filter(file => selected.includes(file._id))
    const value = (row:FMFile, col:string) => {
        switch(col){
        case "size":        return FMFile.isDir(row) ? "" : size(row.size)
        case "updated":     return Utils.formatDate("yyyy-MM-dd hh:mm ampm", new Date(row.updated))
        case "createdBy":   return Usernames.i.row(row.createdBy)?.label || ""
        default:            return (row as any)[col]
        }
    }
    const label = (col:string) => LABELS[col]
    const sort = (col:string,dir:number) => (left:FMFile,right:FMFile) => {
        const lDir = FMFile.isDir(left), rDir = FMFile.isDir(right)
        if (lDir && !rDir) return -1
        else if (rDir && !lDir) return 1
        return value(left, col) < value(right,col) ? -dir : dir
    }
    const onMenu = (file:FMFile) => (e:any) => {
        e.preventDefault()
        e.stopPropagation();

        let files:FMFile[]
        if (selected.length==0) {
            onToggle(file)(true)
            files = [file]
        }
        else files = selectedFiles()
        setMenu({files,x:e.clientX,y:e.clientY})
    }
    const onOpen = (file:FMFile) => (e:any) => {
        e.preventDefault()
        FMFile.isDir(file) && setPath(`/${file._id}`)
    }
    const onConfirmDelete = async (files:FMFile[], recursive:boolean) => {
        try {
            await Files.i.deleteFile(files, recursive)
            setSelected([])
        }catch(e){
            setSnackbar(e)
        }
    }
    const onDelete = (files:FMFile[]) => {
        closeMenu()
        openDialog({id:"confirm",children:<ConfirmDelete files={files} onConfirm={onConfirmDelete}/>})
    }
    const rowProps = (row:FMFile) => ({onContextMenu:onMenu(row), onClick:onSelect(row), onDoubleClick:onOpen(row)})
    const onSelect = (row:FMFile) => () => setSelected(selected => {
        if (selected.includes(row._id)) _.pull(selected, row._id)
        else selected.push(row._id)
        return [...selected]
    })
    const onToggle = (row:FMFile) => (on:boolean) => setSelected(selected => {
        if (on) {
            if (selected.includes(row._id)) return selected
            else return [...selected,row._id]
        }
        else {
            _.pull(selected, row._id)
            return [...selected]
        }
    })
    const cellRenderer = (row:FMFile, col:string) => {
        if (col=="name") return <FileName file={row} selected={selected.includes(row._id)} onToggle={onToggle(row)}/>
        else return value(row, col)
    }
    const onPreview = (file:FMFile) => {
        closeMenu()
        setHash("preview/" + file._id)
    }
    const closeMenu = () => setMenu(undefined)
    const onShare = async (files:FMFile[], duration:number) => {
        try {
            const url = await FManApi.i.shareFiles(files.map(file => file._id), duration)
            openDialog({id:"share",children:<ShareDlg url={url}/>})
        }catch(e){
            setSnackbar(e)
        }
    }
    const onContextMenu = (e:any) => {
        e.preventDefault()
        if (selected.length) setMenu({files:selectedFiles(), x:e.clientX, y:e.clientY})
    }
    const contextMenu = menu ? <FileMenu menu={menu} onClose={closeMenu} onDelete={onDelete} onPreview={onPreview} onShare={onShare}/> : null
    const selectionMenu = selected.length ? <SelectionMenu files={selectedFiles()} onDelete={onDelete} onPreview={onPreview} onShare={onShare}/> : null
    return <div id="filelist">
        {selectionMenu}
        <div className="filesContainer">
        <GTable tableName="files" rows={props.files} bodyRowProps={rowProps} bodyCell={cellRenderer} cols={COLS} value={value} label={label} sorter={sort} onContextMenu={onContextMenu}/>
        {contextMenu}
        </div>
    </div>
}